import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep } from 'lodash';

import { mapContentForEditing } from 'src/data/brief';
import { CONTENT_TYPE } from 'src/data/contentType';
import { generateProjectName, getBriefTypeByProjectType } from 'src/data/project';
import { validateUrl } from 'src/data/validation';
import { createProject } from 'src/graphql/project';
import { getQueryParamValue } from 'src/lib/browser';
import { parseErrors } from 'src/lib/graphql';
import { hashString } from 'src/lib/string';
import { ROUTE_PARAMS } from 'src/routes';

import { alignLongFormProjectTool, waitForProjectCreation } from './utils';

const NAME = 'page-project';

const INITIAL_STATE = {
  projectId: null,
  projectName: '',
  projectType: '',
  contentTaxonomyId: null,

  briefType: null,
  briefUrl: null,
  savedBriefContent: null,
  briefContent: null,
  briefDebugData: null,
  briefCreationModalOpened: false,

  loadingProjectError: null,

  errorBriefGeneral: null,
  errorBriefGeneralCode: null,

  creatingProject: false,
  generatingMultiPrompt: false,

  newProjectIds: [],
  textScoresMap: {},
};

export const PRIVATE_ACTIONS = {
  initProject:
    (project) =>
    ({ setState }) => {
      setState({
        projectId: project?.id,
        projectName: project?.name,
        projectType: project?.type,
        contentTaxonomyId: project?.contentTaxonomy?.id,
        loadingProjectError: null,
      });
    },

  initBrief:
    (project) =>
    ({ setState }) => {
      if (!project) {
        return;
      }

      const { brief } = project;
      if (!brief) {
        return;
      }

      const briefContent = mapContentForEditing(brief);
      setState({
        briefType: brief.type,
        briefContent,
        briefUrl: brief.url,
        savedBriefContent: cloneDeep(briefContent),
        briefDebugData: brief.debugData,
      });
    },

  recoverFromCreationError:
    (error) =>
    ({ setState }) => {
      setState({
        errorBriefGeneral: parseErrors(error).join('\n'),
        errorBriefGeneralCode: error.code || null,
      });
    },
};

/**
 * Public available actions
 */
const ACTIONS = {
  init:
    (project) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.initProject(project));
      dispatch(PRIVATE_ACTIONS.initBrief(project));
    },

  getProjectName:
    () =>
    ({ getState }) => {
      return getState().projectName;
    },
  setProjectName:
    (name) =>
    ({ setState }) => {
      setState({ projectName: name });
    },

  openBriefCreationModal:
    () =>
    ({ setState }) => {
      setState({ briefCreationModalOpened: true });
    },

  setLoadingProjectError:
    (error) =>
    ({ setState }) => {
      setState({ loadingProjectError: error });
    },

  createCommonProject:
    ({ projectType, generationTool, customerMultiPromptTemplateId }) =>
    ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      // Clear error
      setState({
        errorBriefGeneral: null,
        errorBriefGeneralCode: null,
      });

      const projectFolderId = getQueryParamValue(ROUTE_PARAMS.folder) || null;
      const brief = {
        type: getBriefTypeByProjectType(projectType),
      };

      const name = generateProjectName(brief);

      const alignedTool = alignLongFormProjectTool({
        projectType,
        generationTool,
        customerMultiPromptTemplateId,
      });

      return createProject({
        customerId: currentCustomerId,
        projectType,
        brief,
        workspaceId: currentWorkspaceId,
        projectFolderId,
        name,
        customerMultiPromptTemplateId,
        generationTool: alignedTool,
      });
    },
  createWtmProject:
    () =>
    ({ getState, setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      const { projectType, briefType, briefUrl, briefId } = getState();

      setState({
        creatingProject: true,
        errorBriefGeneral: null,
        errorBriefGeneralCode: null,
      });

      const brief = {
        type: briefType,
      };

      return createProject({
        customerId: currentCustomerId,
        projectType,
        brief,
        workspaceId: currentWorkspaceId,
        briefId,
        url: briefUrl,
      })
        .then((project) => waitForProjectCreation(project.id))
        .catch((error) => {
          dispatch(PRIVATE_ACTIONS.recoverFromCreationError(error));

          throw error;
        })
        .finally(() => {
          setState({ creatingProject: false });
        });
    },

  validateWtmProject:
    () =>
    ({ getState, setState }) => {
      const { projectType, briefUrl } = getState();

      // Validate only "URL" first, if it's not valid, no use in validating other fields
      const validationUrl = validateUrl(briefUrl, {
        projectType,
        contentType: CONTENT_TYPE.product,
      });
      if (!validationUrl.valid) {
        setState({ errorBriefUrl: validationUrl.messages.join() });
        return false;
      }

      // Clear errors
      setState({
        errorBriefUrl: null,
      });

      return true;
    },

  updateBriefUrl:
    (value) =>
    ({ setState }) => {
      setState({
        errorBriefUrl: null,
        errorBriefGeneral: null,
        errorBriefGeneralCode: null,
        briefUrl: value,
      });
    },

  isCreatingProject:
    () =>
    ({ getState }) => {
      return getState().creatingProject;
    },

  getBriefUrl:
    () =>
    ({ getState }) => {
      return getState().briefUrl;
    },

  isNewProject:
    () =>
    ({ getState }) => {
      const { projectId, newProjectIds } = getState();
      return newProjectIds.includes(projectId);
    },

  markNewProject:
    (projectId) =>
    ({ getState, setState }) => {
      const { newProjectIds } = getState();

      setState({ newProjectIds: [...newProjectIds, projectId] });
    },

  setGeneratingMultiPrompt:
    (value) =>
    ({ setState }) => {
      setState({ generatingMultiPrompt: value });
    },

  getGeneratingMultiPrompt:
    () =>
    ({ getState }) => {
      return getState().generatingMultiPrompt;
    },

  getTextScore:
    (assetType, text) =>
    ({ getState }) => {
      const { textScoresMap } = getState();
      const key = textScoreMapKey(assetType, text);
      return textScoresMap[key];
    },
  setTextScore:
    (assetType, text, score) =>
    ({ getState, setState }) => {
      const { textScoresMap } = getState();
      const key = textScoreMapKey(assetType, text);
      const newMap = produce(textScoresMap, (draft) => {
        draft[key] = score;
      });
      setState({ textScoresMap: newMap });
    },
};

export const PageStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});

const textScoreMapKey = (assetType, text) => `${assetType}-${hashString(text)}`;
